import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ExternalLink, ChevronRight, Loader2, Building2, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AdminLayout } from "../components/AdminLayout";

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newSaasId, setNewSaasId] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/organizations").then((r) => setOrgs(r.data));
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!newName.trim() || !newSaasId.trim()) return;
    setCreating(true);
    try {
      await api.post("/admin/organizations", { name: newName, saas_org_id: newSaasId.trim() });
      setShowCreate(false);
      setNewName("");
      setNewSaasId("");
      await load();
    } finally {
      setCreating(false);
    }
  };

  return (
    <AdminLayout
      title="Organisations"
      subtitle="Paramétrage du branding et publication des formulaires par tenant"
      actions={
        user?.role === "super_admin" && (
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-700"
            data-testid="create-org-button"
          >
            <Plus className="h-4 w-4" /> Nouvelle organisation
          </button>
        )
      }
    >
      {orgs === null ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : orgs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
          <Building2 className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Aucune organisation accessible.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="organizations-grid">
          {orgs.map((org) => (
            <button
              key={org.id}
              onClick={() => navigate(`/admin/org/${org.id}`)}
              className="group rounded-xl border border-slate-300 bg-white p-5 text-left transition-all hover:border-slate-400 hover:shadow-md"
              data-testid={`org-card-${org.public_token}`}
            >
              <div className="flex items-start justify-between">
                <span
                  className="flex h-11 w-11 items-center justify-center rounded-lg text-white"
                  style={{ background: org.branding?.primary_color || "#0F172A" }}
                >
                  {org.branding?.logo_url ? (
                    <img src={org.branding.logo_url} alt="" className="h-7 w-7 rounded object-contain" />
                  ) : (
                    <Building2 className="h-5 w-5" />
                  )}
                </span>
                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    org.published
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                  data-testid={`org-status-${org.public_token}`}
                >
                  {org.published ? "Publié" : "Brouillon"}
                </span>
              </div>
              <h2 className="mt-4 font-display text-base font-semibold text-slate-900">{org.name}</h2>
              <p className="mt-1 font-mono text-xs text-slate-400">{org.public_token}</p>
              <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-sm">
                <span className="text-slate-500">
                  <span className="font-semibold text-slate-900">{org.total_requests}</span> demandes
                </span>
                <span className="flex items-center gap-1 text-slate-400 group-hover:text-slate-700">
                  Gérer <ChevronRight className="h-4 w-4" />
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="create-org-modal">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold text-slate-900">Nouvelle organisation</h3>
              <button onClick={() => setShowCreate(false)} aria-label="Fermer">
                <X className="h-5 w-5 text-slate-400" />
              </button>
            </div>
            <label htmlFor="new-org-name" className="mb-1.5 block text-sm font-medium text-slate-700">
              Nom de l'organisation
            </label>
            <input
              id="new-org-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="mb-4 w-full rounded-md border border-slate-300 px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
              data-testid="new-org-name-input"
            />
            <label htmlFor="new-org-saas" className="mb-1.5 block text-sm font-medium text-slate-700">
              Nom système de l'organisation Moovapps
            </label>
            <input
              id="new-org-saas"
              value={newSaasId}
              onChange={(e) => setNewSaasId(e.target.value)}
              required
              aria-describedby="new-org-saas-help"
              placeholder="SOCIETEMERE"
              className="mb-1.5 w-full rounded-md border border-slate-300 px-3 py-2.5 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
              data-testid="new-org-saas-input"
            />
            <p id="new-org-saas-help" className="mb-5 text-xs text-slate-500">
              Tel qu'il apparaît dans l'annuaire Moovapps. Les demandes sont rattachées à cette organisation.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCreate(false)}
                className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Annuler
              </button>
              <button
                onClick={create}
                disabled={creating || !newName.trim() || !newSaasId.trim()}
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                data-testid="confirm-create-org"
              >
                {creating && <Loader2 className="h-4 w-4 animate-spin" />} Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
